import type { ProductListing, ProductListingGroup } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { DataTableDisclosure } from "@/components/patterns/DataTable";
import { ErrorState } from "@/components/shared/error-state";
import {
  ProductAdvertisingStatusBadge,
  ProductThumbnail,
} from "@/components/shared/product-thumbnail";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableRow } from "@/components/ui/table";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import { cn } from "@/lib/utils";
import { InventoryQuantity } from "./InventoryQuantity";
import { AmazonProductLink, ProductRowsSkeleton } from "./PickerParts";
import {
  cannotAddListing,
  formatListingPrice,
  isExpandableGroup,
  listingBlockedReason,
  matchesProductIssue,
  type ProductInventoryGate,
  selectedProductFromListing,
} from "./product-listing-helpers";
import type { ProductListingBrowse } from "./use-product-listing-browse";
import type { ProductSelection } from "./use-product-selection";

interface UnifiedGroupRowProps {
  group: ProductListingGroup;
  browse: ProductListingBrowse;
  selection: ProductSelection;
  selectionIssues: CampaignCreationInvalidProduct[];
  productRefreshLoading: boolean;
  marketplaceId: string;
  inventory: ProductInventoryGate;
}

/**
 * A variation group in the unified table: single-SKU groups render as a selectable listing,
 * multi-SKU groups expand into their child listing rows.
 */
export function UnifiedGroupRow({
  group,
  browse,
  selection,
  selectionIssues,
  productRefreshLoading,
  marketplaceId,
  inventory,
}: UnifiedGroupRowProps) {
  const { t, i18n } = useTranslation("campaignCreation");
  const { activeGroup, groups, listings, hasSkuFilter, searchSettling, showGroup } = browse;
  const { selected, selectedBySku, matchingSelected, matchingCurrent, matchingSettled } = selection;
  const groupSelection = hasSkuFilter ? (matchingCurrent ? matchingSelected : []) : selected;
  const selectedInGroup = groupSelection.filter(
    (product) => product.variationGroupKey === group.variationGroupKey,
  );
  const selectableCount = group.selectableSkuCount ?? group.matchingSkuCount;
  const groupIndeterminate = selectedInGroup.length > 0 && selectedInGroup.length < selectableCount;
  const expandable = isExpandableGroup(group);
  const expanded = expandable && activeGroup === group.variationGroupKey;
  const singleListing = expandable ? null : group.singleListing;
  const singleStockBlocked = singleListing
    ? Boolean(
        listingBlockedReason({ ...singleListing, isActive: group.activeSkuCount > 0 }, inventory),
      )
    : false;
  const singleInvalid = singleListing
    ? selectionIssues.find((candidate) => matchesProductIssue(candidate, singleListing))
    : undefined;
  const singlePrice = singleListing
    ? formatListingPrice(singleListing.priceAmount, singleListing.priceCurrency, i18n.language)
    : null;
  const displayTitle = singleListing?.title ?? group.title;
  const displayImage = singleListing?.imageUrl ?? group.imageUrl;
  const toggleExpanded = () => showGroup(expanded ? null : group.variationGroupKey);

  return (
    <>
      <TableRow
        aria-expanded={expandable ? expanded : undefined}
        aria-selected={singleListing ? selectedBySku.has(singleListing.sku) : undefined}
        className={cn(
          expandable && "cursor-pointer",
          "bg-surface hover:bg-subtle/70",
          singleInvalid && "bg-subtle/50 hover:bg-subtle/70",
        )}
        onClick={expandable ? toggleExpanded : undefined}
      >
        <TableCell onClick={(event) => event.stopPropagation()}>
          <Checkbox
            aria-label={t("products.selectParent", {
              parent: group.variationGroupKey,
            })}
            disabled={
              selection.bulkPending ||
              (!browse.bulkSelectable && !singleListing) ||
              (hasSkuFilter && !matchingSettled && !singleListing) ||
              selectableCount === 0 ||
              singleStockBlocked ||
              groups.isError ||
              groups.isPlaceholderData ||
              searchSettling
            }
            checked={
              singleListing
                ? selectedBySku.has(singleListing.sku)
                : selectedInGroup.length > 0 && selectedInGroup.length === selectableCount
            }
            indeterminate={!singleListing && groupIndeterminate}
            onCheckedChange={(checked) => {
              if (checked && singleStockBlocked) return;
              if (checked && singleListing) {
                // The group response already identifies its only SKU. A
                // second bulk lookup adds latency and can make this
                // checkbox fail while the catalog is otherwise usable.
                selection.addProducts(
                  [
                    selectedProductFromListing({
                      ...singleListing,
                      variationGroupKey: group.variationGroupKey,
                    }),
                  ],
                  "search",
                );
              } else if (checked && !groupIndeterminate) {
                void selection.selectFiltered(group.variationGroupKey);
              } else {
                // Group counts follow the browse filters while bulk
                // select only adds in-stock SKUs, so a group holding
                // unavailable children can never fill up. Clicking a
                // partial group clears it rather than re-selecting
                // the same SKUs forever.
                selection.removeSkus(
                  singleListing
                    ? [singleListing.sku]
                    : selectedInGroup.map((product) => product.sku),
                );
              }
            }}
          />
        </TableCell>
        <TableCell className="whitespace-normal py-2.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <ProductThumbnail
              imageUrl={displayImage}
              label={displayTitle ?? group.variationGroupKey}
              asin={singleListing?.asin ?? group.parentAsin}
              sku={singleListing?.sku}
              productStatus={singleListing?.productStatus}
              compact
            />
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold">
                {displayTitle ?? group.variationGroupKey}
              </p>
              {singleListing ? (
                <p className="flex min-w-0 items-center gap-1 font-mono text-[11px] leading-4 text-muted-foreground">
                  <span className="truncate">{singleListing.sku}</span>
                  <span aria-hidden="true">·</span>
                  <span className="shrink-0">{singleListing.asin}</span>
                  <AmazonProductLink asin={singleListing.asin} marketplaceId={marketplaceId} />
                </p>
              ) : (
                <p className="truncate font-mono text-[11px] leading-4 text-muted-foreground">
                  {group.parentAsin ?? group.variationGroupKey}
                  {" · "}
                  {t("products.skuCount", {
                    count: group.matchingSkuCount,
                  })}
                </p>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell className="text-xs">
          {singleListing
            ? t(`products.listingRole.${singleListing.listingRole ?? "unknown"}`)
            : t(`products.groupKind.${group.groupKind ?? "unknown"}`)}
        </TableCell>
        <TableCell>
          {singleListing ? (
            <ProductAdvertisingStatusBadge status={singleListing.productStatus} />
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-xs font-medium">
          {singleListing ? (singlePrice ?? t("products.priceUnknown")) : "—"}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {singleListing ? (
            <InventoryQuantity listing={singleListing} loading={productRefreshLoading} />
          ) : (
            t("products.skuCount", { count: selectableCount })
          )}
        </TableCell>
        <TableCell>
          {expandable ? (
            <DataTableDisclosure
              expanded={expanded}
              label={
                expanded
                  ? t("products.table.collapse", {
                      product: group.title ?? group.variationGroupKey,
                    })
                  : t("products.table.expand", {
                      product: group.title ?? group.variationGroupKey,
                    })
              }
              onToggle={toggleExpanded}
            />
          ) : null}
        </TableCell>
      </TableRow>
      {expanded && listings.isError ? (
        <TableRow>
          <TableCell colSpan={7}>
            <ErrorState message={t("products.loadError")} onRetry={() => void listings.refetch()} />
          </TableCell>
        </TableRow>
      ) : null}
      {expanded && (listings.isPending || listings.isPlaceholderData) ? (
        <TableRow>
          <TableCell colSpan={7}>
            <ProductRowsSkeleton label={t("products.loading")} />
          </TableCell>
        </TableRow>
      ) : null}
      {expanded &&
        !listings.isPlaceholderData &&
        listings.data?.data
          .filter((listing) => listing.listingRole !== "parent")
          .map((listing) => (
            <UnifiedListingRow
              key={listing.sku}
              listing={listing}
              browse={browse}
              selection={selection}
              selectionIssues={selectionIssues}
              productRefreshLoading={productRefreshLoading}
              marketplaceId={marketplaceId}
              inventory={inventory}
            />
          ))}
    </>
  );
}

function UnifiedListingRow({
  listing,
  browse,
  selection,
  selectionIssues,
  productRefreshLoading,
  marketplaceId,
  inventory,
}: Omit<UnifiedGroupRowProps, "group"> & { listing: ProductListing }) {
  const { t, i18n } = useTranslation("campaignCreation");
  const selectedRow = selection.selectedBySku.has(listing.sku);
  const invalid = selectionIssues.find((candidate) => matchesProductIssue(candidate, listing));
  const price = formatListingPrice(listing.priceAmount, listing.priceCurrency, i18n.language);
  return (
    <TableRow
      aria-selected={selectedRow}
      className={cn("bg-surface hover:bg-subtle/70", invalid && "bg-subtle/50 hover:bg-subtle/70")}
    >
      <TableCell>
        <Checkbox
          aria-label={t("products.selectSku", {
            sku: listing.sku,
          })}
          checked={selectedRow}
          disabled={
            (selection.bulkPending && !selectedRow) ||
            cannotAddListing(listing, browse.listingAdProduct, inventory) ||
            browse.searchSettling
          }
          onCheckedChange={(checked) => {
            if (checked) selection.addProducts([selectedProductFromListing(listing)], "search");
            else selection.removeSkus([listing.sku]);
          }}
        />
      </TableCell>
      <TableCell className="whitespace-normal py-2.5">
        <div className="flex min-w-0 items-center gap-2.5 pl-8">
          <ProductThumbnail
            imageUrl={listing.imageUrl}
            label={listing.title ?? listing.asin}
            asin={listing.asin}
            sku={listing.sku}
            productStatus={listing.productStatus}
            compact
          />
          <div className="min-w-0">
            <p className="truncate text-xs font-medium">{listing.title ?? t("products.noTitle")}</p>
            <p className="flex min-w-0 items-center gap-1 font-mono text-[11px] leading-4 text-muted-foreground">
              <span className="truncate">{listing.sku}</span>
              <span aria-hidden="true">·</span>
              <span className="shrink-0">{listing.asin}</span>
              <AmazonProductLink asin={listing.asin} marketplaceId={marketplaceId} />
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-xs">
        {t(`products.listingRole.${listing.listingRole ?? "unknown"}`)}
      </TableCell>
      <TableCell>
        <ProductAdvertisingStatusBadge status={listing.productStatus} />
      </TableCell>
      <TableCell className="text-xs font-medium">{price ?? t("products.priceUnknown")}</TableCell>
      <TableCell className="whitespace-normal text-xs text-muted-foreground">
        <InventoryQuantity listing={listing} loading={productRefreshLoading} />
      </TableCell>
      <TableCell />
    </TableRow>
  );
}
